import React, { useState } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import { getTrustedDeviceToken } from "../../api/userDevices";
import logger from "../../utils/logger.js";
import {
  LogIn,
  Mail,
  Lock,
  Check,
  AlertCircle,
  Shield,
} from "lucide-react";
import "./Login.css";

export default function Login() {
  const { login, isLogged } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [trustDevice, setTrustDevice] = useState(() => Boolean(getTrustedDeviceToken()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const labels = {
    en: {
      title: "Login to your account",
      subtitle: "Welcome back to Mousa Group.",
      email: "Email",
      password: "Password",
      submit: "Login",
      submitting: "Logging in...",
      errorDefault: "Login failed, please try again.",
      haveNoAccount: "Don't have an account?",
      register: "Sign up",
      trustDevice: "Trust this device",
      trustDeviceHint: "Only save this device if it is personal and secure.",
      forgot: "Forgot password?",
      resend: "Resend verification",
      sending: "Sending...",
      resentSuccess: "A new verification email has been sent.",
      resendFailed: "Failed to resend verification email.",
    },
    ar: {
      title: "تسجيل الدخول إلى حسابك",
      subtitle: "مرحباً بعودتك إلى Mousa Group.",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      submit: "دخول",
      submitting: "جارٍ تسجيل الدخول...",
      errorDefault: "فشل تسجيل الدخول، حاول مرة أخرى.",
      haveNoAccount: "ليس لديك حساب؟",
      register: "إنشاء حساب",
      trustDevice: "وثّق هذا الجهاز",
      trustDeviceHint: "احفظ هذا الجهاز فقط إذا كان جهازك الشخصي والآمن.",
      forgot: "نسيت كلمة المرور؟",
      resend: "إعادة إرسال التفعيل",
      sending: "جاري الإرسال...",
      resentSuccess: "تم إرسال رابط تفعيل جديد إلى بريدك الإلكتروني.",
      resendFailed: "فشل في إعادة إرسال بريد التفعيل.",
    },
  }[lang];

  if (isLogged) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setResent(false);
    setShowResend(false);
    setSubmitting(true);

    try {
      const res = await login(email, password, { trustDevice });

      if (!res.ok) {
        const msg = res.message || labels.errorDefault;

        setError(msg);
        setSubmitting(false);

        if (String(msg).toLowerCase().includes("verify")) {
          setShowResend(true);
          localStorage.setItem("verify_email", email);
        }

        return;
      }

      navigate(from, { replace: true });
    } catch (err) {
      logger.error("Login submit error:", err);
      setError(labels.errorDefault);
      setSubmitting(false);
    }
  }

  async function handleResendVerification() {
    try {
      setResending(true);
      setResent(false);
      setError("");

      await api.apiPost("/resend-verification", {
        email,
      });

      localStorage.setItem("verify_email", email);
      setResent(true);
    } catch (err) {
      logger.error("Resend verification error:", err);
      setError(err?.response?.data?.message || labels.resendFailed);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="login-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-header-icon">
              <LogIn size={24} strokeWidth={2} />
            </div>
            <h1 className="login-header-title">{labels.title}</h1>
            <p className="login-header-subtitle">{labels.subtitle}</p>
          </div>

          {error && (
            <div className="login-message error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {showResend && (
            <div className="login-resend-section">
              <button
                type="button"
                className="login-button-resend"
                disabled={resending || !email.trim()}
                onClick={handleResendVerification}
              >
                {resending ? labels.sending : labels.resend}
              </button>

              {resent && (
                <div className="login-message success">
                  <Check size={16} />
                  <span>{labels.resentSuccess}</span>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="login-form">
            <div className="login-form-group">
              <label className="login-form-label">{labels.email}</label>
              <div className="login-input-wrapper">
                <div className="login-input-icon">
                  <Mail size={18} strokeWidth={2} />
                </div>
                <input
                  type="email"
                  className="login-input"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                    setResent(false);
                  }}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="login-form-group">
              <label className="login-form-label">{labels.password}</label>
              <div className="login-input-wrapper">
                <div className="login-input-icon">
                  <Lock size={18} strokeWidth={2} />
                </div>
                <input
                  type="password"
                  className="login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder={isAr ? "••••••••" : "Your password"}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div className="login-checkbox-group">
                <input
                  className="login-checkbox-input"
                  type="checkbox"
                  id="trustDevice"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                <label className="login-checkbox-label" htmlFor="trustDevice">
                  <span className="login-checkbox-text">
                    <Shield size={14} style={{ display: "inline-block", marginRight: "4px" }} />
                    {labels.trustDevice}
                  </span>
                  <p className="login-checkbox-hint">{labels.trustDeviceHint}</p>
                </label>
              </div>

              <div className="login-footer-links">
                <Link to="/forgot-password" className="login-footer-link">
                  {labels.forgot}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className="login-button-submit"
              disabled={submitting}
            >
              <LogIn size={16} />
              <span>{submitting ? labels.submitting : labels.submit}</span>
            </button>
          </form>

          <div className="login-signup-footer">
            <span>{labels.haveNoAccount} </span>
            <Link to="/register" className="login-signup-link">
              {labels.register}
            </Link>
          </div>
        </div>

        <p className="login-copyright">
          © {new Date().getFullYear()} Mousa Group LLC
        </p>
      </div>
    </div>
  );
}
